import { Injectable, NgZone } from '@angular/core'
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subscription,
    catchError,
    mergeWith,
    of,
    switchMap,
    tap,
} from 'rxjs'
import { NumberPublisher } from '@app/domain/NumberPublisher'
import { BookingCommunicationService } from '@services/booking-communication.service'
import { ContextService } from '@services/context.service'

@Injectable({
    providedIn: 'root',
})
export class MessagesUnreadCountPublisher implements NumberPublisher {

    private number = new BehaviorSubject<number>(0)
    public number$ = this.number.asObservable()
    private refreshRateSeconds = 60 * 5
    private pollingSubscription: Subscription | null = null
    private refreshTrigger$ = new ReplaySubject<void>(1)

    constructor(
        private bookingCommunicationService: BookingCommunicationService,
        private contextService: ContextService,
        private zone: NgZone,
    ) { }

    startPolling() {
        if (this.pollingSubscription) {
            return
        }
        this.pollingSubscription = this.zone.runOutsideAngular(() => this.makePoll().subscribe())
        this.refreshNow()
    }

    refreshNow() {
        this.refreshTrigger$.next()
    }

    private makePoll() {
        return this.refreshTrigger$
            .pipe(
                mergeWith(this.emitPeriodically()),
                switchMap(() => this.getUnreadMessageCount()),
                catchError(() => of(0)),
                tap(unreadCount => {
                    this.zone.run(() => this.number.next(unreadCount))
                })
            )
    }

    private emitPeriodically() {
        return new Observable((observer) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null
            const emitFunction = () => {
                observer.next(undefined)
                timeoutId = setTimeout(emitFunction, this.refreshRateSeconds * 1000)
            }
            emitFunction()
            return () => {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId)
                }
            }
        })
    }

    private getUnreadMessageCount(): Observable<number> {
        const organisation = this.contextService.getOrganisation()
        const business = this.contextService.getBusiness()
        const venue = this.contextService.getVenue()
        if (organisation === null || business === null || venue === null) {
            return of(0)
        }

        return this.bookingCommunicationService.getUnreadMessageCount(
            organisation.id,
            business.id,
            venue.id,
        )
    }
}
